'use client';

import {
  Button,
  Divider,
  IcArrowRight,
  IcCopy,
  Image,
  ImageCircleItem,
  Typography,
} from '@musinsa/mds';
import { Tooltip } from '@musinsa/mds-ui';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import type { ProposalItem } from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { pathsMusinsa, useCuratorProxyEnabled } from '../../common';
import * as InfiniteList from '../../common/components/InfiniteList';
import { useCouponLinkCopy, useCuratorSnackbar, useNavigate } from '../../common/hooks';
import { isTooltipShown, markTooltipShown } from '../../common/utils';
import { curatorQueries } from '../../query-factory';
import { useProposals } from '../hooks/useProposals';
import {
  CAMPAIGN_PROPOSAL_STATUS_APPLIED_TAB,
  isMusinsaService,
  trackBrandProposalAppliedGoodsClickMusinsa,
  trackBrandProposalCampaignClickMusinsa,
  trackBrandProposalCopyAppliedLinkClickMusinsa,
  trackBrandProposalCopyKeywordClickMusinsa,
} from '../utils/campaignProposalMusinsaAnalytics';
import { EmptyState } from './common/EmptyState';

const RESTRICTED_TOOLTIP_STORAGE_KEY = 'tooltip-curator-proposal-applied-goods-restricted';
const RESTRICTED_TOOLTIP_DURATION_MS = 5000;

interface ProposalListProps {
  status: 'WAITING' | 'APPROVED' | 'COMPLETED' | 'REJECTED';
  progressStatus?: 'UPCOMING' | 'ONGOING' | 'COMPLETED' | 'CANCELED';
  sort?: 'LATEST' | 'OLDEST' | 'BRAND_NAME' | 'HIGH_DISCOUNT' | 'MANY_GOODS' | 'ENDING_SOON';
  onTotalCountChange?: (count: number) => void;
}

const EMPTY_TEXT: Record<string, { title: string; subTitle: string }> = {
  WAITING: {
    title: '제안 받은 할인 쿠폰 캠페인이 없습니다.',
    subTitle: '브랜드가 제안한 협업을 여기서 확인하실 수 있어요.',
  },
  APPROVED: {
    title: '수락한 할인 쿠폰 캠페인이 없습니다.',
    subTitle: '브랜드가 제안한 협업을 수락하면 여기서 확인하실 수 있어요.',
  },
  COMPLETED: {
    title: '완료한 할인 쿠폰 캠페인이 없습니다.',
    subTitle: '브랜드와 협업을 완료하면 여기서 확인하실 수 있어요.',
  },
  REJECTED: {
    title: '거절한 할인 쿠폰 캠페인이 없습니다.',
    subTitle: '브랜드의 협업을 거절하면 여기서 확인하실 수 있어요.',
  },
};

const PROGRESS_LABELS: Record<string, string> = {
  UPCOMING: '캠페인 진행 대기중',
  ONGOING: '진행중',
  COMPLETED: '캠페인 진행 완료',
  CANCELED: '캠페인 진행 취소',
};

export const ProposalList = ({
  status,
  progressStatus,
  sort,
  onTotalCountChange,
}: ProposalListProps) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching, isPlaceholderData } =
    useProposals({ status, progressStatus, sort, size: 10 });

  const items = data?.pages.flatMap((page) => page.list) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  // UPCOMING 카드 중 첫 번째에만 툴팁을 노출. (다중 노출 시 시각 노이즈 방지 + storageKey 로 lifetime 1회 보장)
  const firstUpcomingIndex = items.findIndex(
    (i) => status === 'APPROVED' && i.progressStatus === 'UPCOMING' && i.coupon != null,
  );

  useEffect(() => {
    if (data && !isPlaceholderData) {
      onTotalCountChange?.(total);
    }
  }, [total, onTotalCountChange, data, isPlaceholderData]);

  // 실제 데이터가 로드 완료 + 빈 목록일 때만 빈 상태 표시
  if (items.length === 0 && !isFetching && !isPlaceholderData) {
    return (
      <EmptyState
        title={EMPTY_TEXT[status].title}
        subTitle={EMPTY_TEXT[status].subTitle}
        height={421}
      />
    );
  }

  return (
    <div className="overflow-y-auto scrollbar-hide">
      <InfiniteList.Root
        className="box-border relative flex flex-col h-full px-4"
        loadMore={() => fetchNextPage()}
        isPending={isFetchingNextPage}
        hasMore={hasNextPage}
      >
        {items.map((item, index) => {
          const isLastItem = index === items.length - 1;
          return (
            <li key={item.campaignProposalId} className="list-none">
              <ProposalItemCard
                item={item}
                status={status}
                isLast={isLastItem}
                isFirst={index === 0}
                showRestrictedTooltip={index === firstUpcomingIndex}
              />
              {!isLastItem && <Divider variant="line" className="my-3" />}
            </li>
          );
        })}
        <InfiniteList.Trigger />
      </InfiniteList.Root>
    </div>
  );
};

function ProposalItemCard({
  item,
  status,
  isLast,
  isFirst,
  showRestrictedTooltip,
}: {
  item: ProposalItem;
  status: string;
  isLast: boolean;
  isFirst: boolean;
  showRestrictedTooltip: boolean;
}) {
  const { service } = useDeviceInfo();
  const { navigate } = useNavigate();
  const { data: userInfo } = useQuery({ ...curatorQueries.profile.userInfo });
  const curatorProxyEnabled = useCuratorProxyEnabled();
  const snackbar = useCuratorSnackbar();

  const showCouponActions = status === 'APPROVED' && item.coupon != null && curatorProxyEnabled;
  // flag OFF 시 fallback: 기존 키워드 복사 UX. 캠페인 종료/취소 건은 의미 없으므로 제외.
  const showCopyKeyword =
    status === 'APPROVED' &&
    item.coupon != null &&
    !curatorProxyEnabled &&
    item.progressStatus !== 'COMPLETED' &&
    item.progressStatus !== 'CANCELED';
  const couponNo = item.coupon?.couponNo;
  const isMusinsa = isMusinsaService(service);
  const analyticsParams = {
    campaignTitle: `${item.salesRate}% 할인 쿠폰 캠페인 제안`,
    suggestionNo: String(item.campaignProposalId),
    brandId: item.brandId,
    curatorId: userInfo?.snsTitle ?? '',
  };

  // NOTE: webview Safari 정책 회피 위해 사전 fetch 한 short link 를 click 시 동기 복사.
  const copyLink = useCouponLinkCopy({ couponNo, enabled: showCouponActions });
  const handleCopyLink = () => {
    if (isMusinsa) {
      trackBrandProposalCopyAppliedLinkClickMusinsa(analyticsParams);
    }
    copyLink();
  };

  const handleClickAppliedGoods = () => {
    if (couponNo == null || !userInfo?.snsTitle) return;
    if (isMusinsa) {
      trackBrandProposalAppliedGoodsClickMusinsa(analyticsParams);
    }
    navigate(pathsMusinsa.myShopCouponGoods({ shopSlug: userInfo.snsTitle, couponNo }));
  };

  const handleCopyKeyword = () => {
    if (!item.coupon) return;
    navigator.clipboard.writeText(item.coupon.couponKeyword);
    snackbar.show({ message: '쿠폰 키워드를 복사했습니다.' });
    if (isMusinsa) {
      trackBrandProposalCopyKeywordClickMusinsa({
        campaignTitle: discountCouponCampaignTitle,
        suggestionNo: String(item.campaignProposalId),
        brandId: item.brandId,
      });
    }
  };

  const [tooltipVisible, setTooltipVisible] = useState(false);
  useEffect(() => {
    if (!showRestrictedTooltip) return;
    if (isTooltipShown(RESTRICTED_TOOLTIP_STORAGE_KEY)) return;
    setTooltipVisible(true);
  }, [showRestrictedTooltip]);

  const statusText =
    status === 'WAITING'
      ? '캠페인 수락 대기중'
      : status === 'REJECTED'
        ? '캠페인 제안 거절'
        : (PROGRESS_LABELS[item.progressStatus] ?? '');

  const discountCouponCampaignTitle =
    status === 'WAITING'
      ? `${item.salesRate}% 할인 쿠폰 캠페인 제안이 왔어요.`
      : `${item.salesRate}% 할인 쿠폰 캠페인 제안`;

  const formatDate = (dateStr: string) => dateStr.split(/[T ]/)[0]?.replace(/-/g, '.') ?? dateStr;

  const ddayText = getDdayText(item.endAt);
  const showDday =
    (status === 'WAITING' || (status === 'APPROVED' && item.progressStatus === 'ONGOING')) &&
    ddayText;

  const isStatusBlue = item.status === 'APPROVED' && item.progressStatus === 'ONGOING';

  return (
    <div
      className={`flex flex-col gap-3 ${isLast ? 'pb-[72px]!' : 'pb-3'} ${isFirst ? 'pt-6' : 'pt-3'}`}
    >
      <Link
        href={pathsMusinsa.campaignProposalDetail({ query: { id: item.campaignProposalId } })}
        className="flex gap-2 justify-between"
        onClick={() => {
          if (!isMusinsa) return;
          trackBrandProposalCampaignClickMusinsa({
            campaignTitle: discountCouponCampaignTitle,
            appliedTab: CAMPAIGN_PROPOSAL_STATUS_APPLIED_TAB[item.status],
            suggestionNo: String(item.campaignProposalId),
            brandId: item.brandId,
          });
        }}
      >
        <div className="flex w-full min-w-0 gap-3">
          <div className="mt-[11px]! flex-shrink-0">
            <ImageCircleItem size="58" type="logo">
              <Image src={item.logoUrl} alt={item.brandName} />
            </ImageCircleItem>
          </div>
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <div className="relative w-fit">
              <Typography
                size="13"
                weight="semiBold"
                colorScheme={isStatusBlue ? 'blue' : 'gray-600'}
              >
                {statusText}
              </Typography>
              {tooltipVisible && (
                <div className="absolute right-0 -top-[50%]">
                  <Tooltip
                    className="absolute! left-full! top-0! z-50! ml-[11px]!"
                    align="right center"
                    duration={RESTRICTED_TOOLTIP_DURATION_MS}
                    onCloseCallback={() => {
                      markTooltipShown(RESTRICTED_TOOLTIP_STORAGE_KEY);
                      setTooltipVisible(false);
                    }}
                    content={
                      <>
                        대기 중인 캠페인은 적용상품 페이지
                        <br />
                        접근이 제한됩니다.
                      </>
                    }
                  />
                </div>
              )}
            </div>
            <Typography size="13" weight="semiBold" colorScheme="black" className="truncate">
              {item.brandName}
            </Typography>
            <Typography size="13" weight="regular" colorScheme="black">
              {discountCouponCampaignTitle}
            </Typography>
            <Typography size="13" weight="regular" colorScheme="black">
              {formatDate(item.startAt)} ~ {formatDate(item.endAt)}
              {showDday && <span>・ {ddayText}</span>}
            </Typography>
          </div>
        </div>
        <div className="w-4 shrink-0">
          <IcArrowRight strokeColor="gray-500" />
        </div>
      </Link>

      {showCouponActions && (
        <div className="flex gap-[4px]">
          <Button variant="secondary" size={36} className="flex-1" onClick={handleCopyLink}>
            <IcCopy width={16} height={16} strokeColor="black" />
            <Typography variant="body13pxMed" className="ml-0.5">
              적용 상품 링크 복사
            </Typography>
          </Button>
          <Button
            variant="secondary"
            size={36}
            className="flex-1"
            onClick={handleClickAppliedGoods}
          >
            <Typography variant="body13pxMed">적용 상품 페이지</Typography>
          </Button>
        </div>
      )}

      {showCopyKeyword && item.coupon && (
        <Button size={36} variant="secondary" onClick={handleCopyKeyword} className="gap-[2px]">
          <IcCopy width={16} height={16} strokeColor="black" />
          <Typography variant="body13pxMed">
            쿠폰 키워드 복사 ({item.coupon.couponKeyword})
          </Typography>
        </Button>
      )}
    </div>
  );
}

function getDdayText(endAt: string): string | null {
  // NOTE: 쿠폰 적용 상품 페이지(coupon-expiration.util.ts)와 동일 기준:
  // dayjs 로컬 startOf('day') diff 사용. 만료 당일 = D-day (별도 표기), 하루 전 = 1일 전.
  // new Date(dateString) UTC 파싱 + Math.ceil 조합 시 KST 기준 off-by-one 발생하여 수정.
  const today = dayjs().startOf('day');
  const endDate = dayjs(endAt.split(/[T ]/)[0]).startOf('day');
  const diffDays = endDate.diff(today, 'day');
  if (diffDays >= 1 && diffDays <= 7) {
    return `종료 ${diffDays}일 전`;
  }
  return null;
}
